/**
 * React-query views over the session and the caller's account.
 *
 * Both are cached so the route guards can answer "is this person signed in?" and
 * "have they finished the questionnaire?" on every navigation without a network
 * round-trip each time.
 *
 * The session itself comes from Better Auth's own `useSession`, which reads the
 * cookie-backed session. The application's view of the user — role, board, grade,
 * school, teacher subject — comes from `GET /me`, because those live in the
 * database rather than in a token claim. That is the fix for a real old bug:
 * claims lagged a refresh behind a profile save, so a student who had just
 * finished onboarding was sent back into it.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AccountDTO, UserProfileDTO } from '@cwy/config/contracts';
import { apiGet } from '@/lib/api';
import { authClient } from '@/lib/authClient';
import { queryClient } from '@/lib/queryClient';
import { claimsFromAccount, type Claims } from '@/lib/auth';

export const ACCOUNT_KEY = ['me'] as const;
export const PROFILE_KEY = ['me', 'profile'] as const;

/** Better Auth's cookie-backed session. Null when signed out. */
export function useSession() {
  return authClient.useSession();
}

/** The application account: role and scoping, read from the database. */
export function useAccount(enabled: boolean) {
  return useQuery<AccountDTO>({
    queryKey: ACCOUNT_KEY,
    queryFn: () => apiGet<AccountDTO>('/me'),
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useMyProfile(enabled: boolean) {
  return useQuery<UserProfileDTO>({
    queryKey: PROFILE_KEY,
    queryFn: () => apiGet<UserProfileDTO>('/me/profile'),
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
}

/**
 * Drop every cached answer about who is signed in.
 *
 * Called on sign-in and sign-out. Without the removal, the previous user's
 * profile could be read for the next one.
 */
export function resetIdentityQueries(): void {
  queryClient.removeQueries({ queryKey: ACCOUNT_KEY });
  queryClient.removeQueries({ queryKey: PROFILE_KEY });
}

export function invalidateAccount(): void {
  void queryClient.invalidateQueries({ queryKey: ACCOUNT_KEY });
}

/** Re-read the profile, e.g. after onboarding saved it. */
export function invalidateProfile(): void {
  void queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
}

/**
 * The claims, as a REACTIVE read.
 *
 * `decodeClaims()` in lib/auth is a synchronous module-level cache that AuthGate
 * publishes into. That is right for the ~20 components which read it during
 * render deep inside a page — by then it is always populated.
 *
 * It is wrong for the app CHROME. A shell renders in the same commit as the
 * guard that publishes the cache, so it reads `null`, decides it is looking at a
 * student, and never re-renders — the cache is a plain variable, not state. That
 * is what left the platform console showing a school sidebar and no role badge.
 *
 * This hook reads the same `GET /me` query the guard does, so the chrome updates
 * the moment the answer arrives.
 */
export function useClaims(): Claims | null {
  const { data: session } = useSession();
  const account = useAccount(Boolean(session?.user));
  return useMemo(() => claimsFromAccount(account.data ?? null), [account.data]);
}
